import { useAdminSubmissionContext } from '@/providers/AdminSubmissionProvider/AdminSubmissionContext';
import { propertyChanged } from '@/providers/AdminSubmissionProvider/actions';
import { Input } from '@/components/ui/input';
import {
  SettingRowCell,
  SettingRowInput,
  SettingsRow,
} from '@/components/Section';
import { Maybe, VoteForSubmissionData } from '@/generated/graphql';
import { ColorPicker } from '@/components/ColorPicker';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CameraIcon, DotsVerticalIcon, TrashIcon } from '@radix-ui/react-icons';
import { useMemo } from 'react';
import { CropAndUploadDialog } from '@/components/CropAndUploadDialog';
import { InlineConfirmButton } from '@/components/InlineConfirmButton';

export function EditableChoiceRow({
  onDelete,
}: {
  onDelete: (id: string) => void;
}) {
  const { state, dispatch, submission } =
    useAdminSubmissionContext<VoteForSubmissionData>();

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: submission.id,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <SettingsRow ref={setNodeRef} style={style} forceRow>
      <SettingRowCell>
        <div
          className="w-10 text-center text-lg text-muted-foreground hover:cursor-grab hover:text-foreground"
          {...attributes}
          {...listeners}
        >
          <DotsVerticalIcon className="h-6 w-6" />
        </div>
      </SettingRowCell>
      <SettingRowCell>
        <ColorPicker
          value={state.color}
          onChange={(color) => dispatch(propertyChanged('color', color))}
        />
      </SettingRowCell>
      <SettingRowInput>
        <Input
          id={`title-${submission.id}`}
          placeholder="Title"
          value={state.title}
          onChange={(e) => dispatch(propertyChanged('title', e.target.value))}
        />
      </SettingRowInput>
      <SettingRowCell>
        <OptionImageInput
          color={state.color}
          imageUrl={state.optionalImageUrl}
          onChange={(imageUrl) =>
            dispatch(propertyChanged('optionalImageUrl', imageUrl))
          }
        />
      </SettingRowCell>
      <SettingRowCell>
        <InlineConfirmButton
          variant="ghost"
          size="icon"
          onConfirm={() => onDelete(submission.id)}
        >
          <TrashIcon />
        </InlineConfirmButton>
      </SettingRowCell>
    </SettingsRow>
  );
}

function OptionImageInput({
  color,
  imageUrl,
  onChange,
}: {
  color: string;
  imageUrl: Maybe<string> | undefined;
  onChange: (imageUrl: string) => void;
}) {
  const style = useMemo(() => ({ borderColor: color }), [color]);
  return (
    <CropAndUploadDialog ratio={1} onSave={onChange} maxWidth={320}>
      <div
        className="flex h-16 w-16 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 bg-muted"
        style={style}
      >
        {imageUrl ? (
          <img src={imageUrl} className="h-16 w-16" />
        ) : (
          <CameraIcon className="h-6 w-6" />
        )}
      </div>
    </CropAndUploadDialog>
  );
}
